import googleAnalytics from '@analytics/google-analytics';
import googleTagManager from '@analytics/google-tag-manager';
import mixpanelPlugin from '@analytics/mixpanel';
// AIDEV-NOTE: edgetagPlugin + helpers come from the shared @petplate/analytics package now (was apps/subflow/src/lib).
// Subflow keeps its OWN analytics() instance because it uses react-router (useLocation) for route change
// detection, while the shared @petplate/analytics provider is Next.js-only (usePathname).
import {
  appendEdgetagUserId as appendEdgetagUserIdRaw,
  createEdgetagHelpers,
  edgetagPlugin,
  getEdgetagUserId as getEdgetagUserIdRaw,
  identifyContact,
  trackTripleProductView,
  triplePixelPlugin
} from '@petplate/analytics';
import {
  EDGETAG_EDGE_URL,
  ENV,
  GOOGLE_ANALYTICS_MEASUREMENT_ID,
  GTM_CONTAINER_ID,
  MIXPANEL_TOKEN,
  SHOPIFY_CUSTOM_DOMAIN,
  TRIPLE_WHALE_ENABLED
} from '@petplate/settings';
import Analytics, { AnalyticsPlugin } from 'analytics';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// NOTE: Mixpanel plugin only added when MIXPANEL_TOKEN is set (production only)
const plugins: AnalyticsPlugin[] = [
  googleTagManager({
    containerId: GTM_CONTAINER_ID
  }),
  googleAnalytics({
    measurementIds: [GOOGLE_ANALYTICS_MEASUREMENT_ID]
  })
];

if (MIXPANEL_TOKEN) {
  const isProduction = ENV === 'production';
  plugins.push(
    mixpanelPlugin({
      token: MIXPANEL_TOKEN,
      options: {
        // NOTE: sendBeacon transport so events fired right before a navigation/redirect
        // (e.g. /subscription/confirmation loader) survive page unload
        api_transport: 'sendBeacon',
        // NOTE: Session Replay — only enabled in production (BR4)
        record_sessions_percent: isProduction ? 100 : 0,
        record_mask_all_text: false,
        record_mask_all_inputs: false,
        record_block_selector: 'img, video',
        record_idle_timeout_ms: 1800000
      }
    })
  );
}

// AIDEV-NOTE: Edgetag (Blotout) plugin only added when EDGETAG_EDGE_URL is set.
// disableConsentCheck=true today; flip to false once the consent layer ships (Phase F).
if (EDGETAG_EDGE_URL) {
  plugins.push(
    edgetagPlugin({
      edgeURL: EDGETAG_EDGE_URL,
      disableConsentCheck: true
    })
  );
}

if (TRIPLE_WHALE_ENABLED) {
  plugins.push(triplePixelPlugin({ shop: SHOPIFY_CUSTOM_DOMAIN }));
}

export const analytics = Analytics({
  app: 'petplate',
  debug: true,
  plugins
});

// AIDEV-NOTE: Edgetag-shaped track helpers bound to subflow's analytics instance. Plugin-scoped so
// they fire ONLY into the edgetag plugin (not GA4/GTM/Mixpanel). Use these at concrete call sites
// (PetPlansForm, PetPlanConfirmation) instead of trackEvent('addToCart', ...) — the latter would
// also hit GA4/GTM and would not match the canonical Edgetag payload shape.
const edgetagHelpers = createEdgetagHelpers(analytics);
export const trackViewContent = edgetagHelpers.trackViewContent;
export const trackAddToCart = edgetagHelpers.trackAddToCart;
export const trackInitiateCheckout = edgetagHelpers.trackInitiateCheckout;
export const trackLead = edgetagHelpers.trackLead;
export { identifyContact, trackTripleProductView };
export const getEdgetagUserId = getEdgetagUserIdRaw;
export const appendEdgetagUserId = appendEdgetagUserIdRaw;

export const useTrackPageChange = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    analytics.page({
      event: 'pageview'
    });
  }, [pathname]);
};

export const trackEvent = (event: string, properties: unknown) => {
  analytics.track(event, properties);
};

// NOTE: call this when user email/identity becomes available (e.g. email step, sign-in)
// Uses Mixpanel special properties ($email, $name) so they map to People profile fields
export const identifyUser = (userId: string, traits?: Record<string, unknown>) => {
  if (!userId) return;
  const mappedTraits: Record<string, unknown> = { ...traits };
  if (traits?.email && traits.email !== 'undefined') {
    mappedTraits.$email = traits.email;
  }
  if (traits?.name && traits.name !== 'undefined') {
    mappedTraits.$name = traits.name;
  }
  analytics.identify(userId, mappedTraits);
};

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useTrackPageChange();
  return <>{children}</>;
};
